/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import React, { useContext } from 'react';
import { useTranslation } from '@scuf/localization';
import descriptors from '../../../locales/en-US/descriptors';
import './UtilityDetailSummaryComponent.scss';
import {
  formatCurrency,
  formatPriceValue,
  formatUnit,
  getAbsoluteValue
} from '../Utils/Utils';
import { Badge } from '@scuf/common';
import {
  KPI_PERCENTAGE_DECIMAL_PRECISION,
  KPI_VALUE_DECIMAL_PRECISION,
  PERCENT_CHANGE_THRESHOLD
} from '../Utils/Constants';
import { getPercentageColor } from '../../DetailKPIWidgets/PortfolioEnergyUsageViewDetails/PremiumSubscription/AllUtilitiesDetails/BottomSection/TableColumnRenderUtils';
import { IUtilityBillAggregation } from '../../DetailKPIWidgets/UtilityBillViewDetailsComponent/IUtilityBillViewDetails';
import { getConvertedValue } from '../Utils/unitHandling/unitHandling';
import { ApiContext } from '../../../context/ApiContext';

interface IDetailSummaryComponentProps {
  aggregationData: IUtilityBillAggregation;
  measurementUnit: string;
  costCurrency: string;
  forUtility?: boolean;
  originalUnit: string;
  utilityType: string;
  isPrevPeriodForAllMonths?: boolean;
  isPrevPeriodForAllMonthsConsumption?: boolean;
}
const UtilityDetailSummaryComponent = (props: IDetailSummaryComponentProps) => {
  const d = descriptors.energyManagement;
  const [t] = useTranslation();
  const {
    aggregationData,
    measurementUnit,
    costCurrency,
    isPrevPeriodForAllMonths,
    isPrevPeriodForAllMonthsConsumption
  } = props;
  const {
    actualConsumtion,
    baselineConsumption,
    totalConsumptionSavings,
    totalConsumptionSavingsPercentage,
    actualCost,
    baselineCost,
    costSavings,
    costSavingsPercentage,
    actualUnit,
    baselineUnit,
    changeUnit
  } = aggregationData;
  const apiContext = useContext(ApiContext);

  return (
    <div>
      {aggregationData && (
        <div className="emaas-summary-widget-container">
          <div className="summary-cost emaas-detail-summary-card-section section-one">
            <div
              className="summary-actual-cost emaas-summary-value-container"
              style={{ marginLeft: 0 }}
            >
              <div>
                <span className="emaas-summary-value actual-cost">
                  {props.forUtility
                    ? getConvertedValue(
                        props.utilityType,
                        props.originalUnit,
                        actualConsumtion,
                        actualUnit
                      )?.Value?.toFixed(KPI_VALUE_DECIMAL_PRECISION)
                    : actualConsumtion.toFixed(KPI_VALUE_DECIMAL_PRECISION)}
                </span>
                <span className="emaas-summary-unit">
                  {props.forUtility ? actualUnit : measurementUnit}
                </span>
              </div>
              <span className="emaas-summary-title">{t(d.ACTUAL)}</span>
            </div>
            <div className="summary-baseline-cost emaas-summary-value-container">
              <div>
                <span className="emaas-summary-value baseline-cost">
                  {props.forUtility
                    ? getConvertedValue(
                        props.utilityType,
                        props.originalUnit,
                        getAbsoluteValue(baselineConsumption),
                        baselineUnit
                      )?.Value?.toFixed(KPI_VALUE_DECIMAL_PRECISION)
                    : getAbsoluteValue(baselineConsumption).toFixed(
                        KPI_VALUE_DECIMAL_PRECISION
                      )}
                </span>
                <span className="emaas-summary-unit">
                  {props.forUtility ? baselineUnit : measurementUnit}
                </span>
              </div>
              <span className="emaas-summary-title">{t(d.PREVIOUSPERIOD)}</span>
            </div>
            <div className="summary-savings-cost emaas-summary-value-container">
              {(apiContext.showChangeWhenData &&
                isNaN(Number(actualConsumtion))) ||
              isNaN(Number(baselineConsumption)) ||
              Number(actualConsumtion) === 0 ||
              Number(baselineConsumption) === 0 ||
              !isPrevPeriodForAllMonthsConsumption ||
              Math.abs(Number(totalConsumptionSavingsPercentage)) >
                PERCENT_CHANGE_THRESHOLD ? (
                '--'
              ) : (
                <div>
                  <span
                    className="emaas-summary-valuecolorchange cost-saving"
                    style={{
                      color: getPercentageColor(
                        totalConsumptionSavingsPercentage
                      )
                    }}
                  >
                    {props.forUtility
                      ? getConvertedValue(
                          props.utilityType,
                          props.originalUnit,
                          getAbsoluteValue(totalConsumptionSavings),
                          changeUnit
                        )?.Value?.toFixed(KPI_VALUE_DECIMAL_PRECISION)
                      : getAbsoluteValue(totalConsumptionSavings).toFixed(
                          KPI_VALUE_DECIMAL_PRECISION
                        )}
                  </span>
                  <span
                    className="emaas-summary-unitcolorchange"
                    style={{
                      color: getPercentageColor(
                        totalConsumptionSavingsPercentage
                      )
                    }}
                  >
                    {props.forUtility ? changeUnit : measurementUnit}
                  </span>
                  <span
                    className="emaas-saving-percentage"
                    style={{
                      color: getPercentageColor(
                        totalConsumptionSavingsPercentage
                      )
                    }}
                  >
                    <Badge
                      color={
                        totalConsumptionSavingsPercentage > 0
                          ? 'green'
                          : totalConsumptionSavingsPercentage < 0
                          ? 'red'
                          : 'grey'
                      }
                      empty
                    />
                    <span>
                      {' '}
                      {getAbsoluteValue(
                        totalConsumptionSavingsPercentage
                      ).toFixed(KPI_PERCENTAGE_DECIMAL_PRECISION)}
                      %
                    </span>
                  </span>
                </div>
              )}
              <span className="emaas-summary-title">{t(d.CHANGE)}</span>
            </div>
          </div>
          <div className="section-divider"></div>
          <div className="summary-cost emaas-detail-summary-card-section section-two">
            <div className="summary-actual-cost emaas-summary-value-container">
              <div>
                <span className="emaas-summary-value actual-cost">
                  {formatCurrency(actualCost, costCurrency)}
                  {/* {costCurrency === 'MYR'
                    ? formatCurrency(actualCost, costCurrency)
                    : formatPriceValue(
                        formatCurrency(actualCost, costCurrency)
                      )} */}
                </span>
                {/* {costCurrency !== 'MYR' && (
                  <span className="emaas-summary-unit">
                    {formatUnit(formatCurrency(actualCost, costCurrency))}
                  </span>
                )} */}
              </div>
              <span className="emaas-summary-title">{t(d.ACTUALCOST)}</span>
            </div>
            <div className="summary-baseline-cost emaas-summary-value-container">
              <div>
                <span className="emaas-summary-value baseline-cost">
                  {formatCurrency(baselineCost, costCurrency)}
                  {/* {costCurrency === 'MYR'
                    ? formatCurrency(baselineCost, costCurrency)
                    : formatPriceValue(
                        formatCurrency(baselineCost, costCurrency)
                      )} */}
                </span>
                {/* {costCurrency !== 'MYR' && (
                  <span className="emaas-summary-unit">
                    {formatUnit(formatCurrency(baselineCost, costCurrency))}
                  </span>
                )} */}
              </div>
              <span className="emaas-summary-title">{t(d.PREVIOUSCOST)}</span>
            </div>
            <div className="summary-savings-cost emaas-summary-value-container">
              {(apiContext.showChangeWhenData && isNaN(Number(actualCost))) ||
              isNaN(Number(baselineCost)) ||
              Number(actualCost) === 0 ||
              Number(baselineCost) === 0 ||
              Math.abs(Number(costSavingsPercentage)) >
                PERCENT_CHANGE_THRESHOLD ||
              !isPrevPeriodForAllMonths ? (
                '--'
              ) : (
                <div style={{ display: 'flex' }}>
                  <div
                    className="emaas-summary-valuechangecolor cost-saving"
                    style={{
                      color: getPercentageColor(costSavings)
                    }}
                  >
                    {formatCurrency(costSavings, costCurrency)}
                    {/* {costCurrency === 'MYR'
                      ? formatCurrency(costSavings, costCurrency)
                      : formatPriceValue(
                          formatCurrency(costSavings, costCurrency)
                        )} */}
                  </div>
                  {/* {costCurrency !== 'MYR' && (
                    <div
                      className="emaas-summary-unitchangecolor"
                      style={{
                        color: getPercentageColor(costSavings)
                      }}
                    >
                      {formatUnit(formatCurrency(costSavings, costCurrency))}
                    </div>
                  )} */}
                  <div
                    className="emaas-saving-percentage"
                    style={{
                      color: getPercentageColor(costSavings)
                    }}
                  >
                    <Badge
                      color={
                        costSavingsPercentage > 0
                          ? 'green'
                          : costSavingsPercentage < 0
                          ? 'red'
                          : 'grey'
                      }
                      empty
                    />
                    <span>
                      {' '}
                      {getAbsoluteValue(
                        (costSavingsPercentage.toFixed(
                          KPI_PERCENTAGE_DECIMAL_PRECISION
                        ) as unknown) as number
                      )}
                      %
                    </span>
                  </div>
                </div>
              )}
              <span className="emaas-summary-title">{t(d.CHANGE)}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UtilityDetailSummaryComponent;
